import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {TableProps} from "@material-ui/core/Table";
import Message from "../message/Message";
import {Paper, TableContainer, Typography} from "@material-ui/core";
import _ from 'lodash';
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import PageWrapper from "../pagewrapper/PageWrapper";
import ToolBar from "../toolbar/ToolBar";

const useStyles = makeStyles(theme => ({
    listItemDataLi: {
        borderBottom: 'solid 1px #e0e0e0',
        '&:nth-child(even)': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
    },
    labelValueDiv: {
        width: '100%',
        display: 'flex',
        borderBottom: 'solid 1px #e0e0e0',
        '& > div': {
            display: 'flex',
            alignItems: 'center'
        },
        '& > div:first-child': {
            width: '30%',
            maxWidth: 200,
            marginRight: theme.spacing(.5),
            padding: theme.spacing(1),
            justifyContent: 'flex-end',
            '& strong': {
                textAlign: 'right'
            }
        },
        '& > div:last-child': {
            flexGrow: 1,
            padding: theme.spacing(1)
        }
    }
}));

interface IColumn<T> {
    label: string;
    field: string;
    className?: string;
    valueGetter?: (value: any | string, row?: T) => JSX.Element | string;
    rendered?: boolean | ((value: any) => boolean);
}

export interface IDataListProps<T> extends TableProps {
    /** Fileiras são um tipo genérico, desconhecido. */
    rows: T[];
    columns: IColumn<T>[];
    comparatorKeys?: string[];
    toolbarContent?: JSX.Element;
    tableContainerProps?: {
        component?: JSX.Element;
        classes?: any;
    };
}

interface ListItemDataProps<T> extends TableProps {
    row: T;
    columns: IColumn<T>[];
}

function ListItemData<T>(props: ListItemDataProps<T>): JSX.Element {
    const {
        row,
        columns
    } = props;
    const classes = useStyles();

    return (
        <>
            {columns.map((column, columnIndex) => {
                const value = column.valueGetter
                    ? column.valueGetter(_.get(row, column.field), row)
                    : _.get(row, column.field);

                return (
                    <div key={'listItemData' + columnIndex}
                         className={classes.labelValueDiv}>
                        <div><strong>{column.label}</strong></div>
                        <div>{value}</div>
                    </div>
                );
            })}
        </>
    );
}

export default function DataList<T>(props: IDataListProps<T>): JSX.Element {
    const {
        rows,
        columns,
        comparatorKeys = [],
        toolbarContent,
        className = '',
        tableContainerProps = {
            component: Paper
        }
    } = props;
    const classes = useStyles();
    const [error, setError] = useState<string>('');

    const getComparatorValue = useCallback(row => {
        return comparatorKeys.reduce((accumulator, key) => accumulator + row[key], '');
    }, [comparatorKeys, comparatorKeys.length]);

    return (
        <>
            {!!toolbarContent && (
                <ToolBar noBorderRadius>{toolbarContent}</ToolBar>
            )}
            {!error && (
                <TableContainer {...tableContainerProps}>
                    <ul className={className}>
                        {rows.map((row, rowIndex) => (
                            <li key={getComparatorValue(row) ? 'rowKey' + getComparatorValue(row) : 'rowKey' + rowIndex}
                                className={classes.listItemDataLi}>
                                <ListItemData row={row} columns={columns} />
                            </li>
                        ))}
                    </ul>
                </TableContainer>
            )}
            {!error && !rows.length && (
                <Message type='info'>Nenhum registro encontrado.</Message>
            )}
            {!!error && (
                <Message type='error'>{error}</Message>
            )}
        </>
    );
}